import type { BannerUploadData } from './types';
import { DeviceType, detectDeviceTypeFromFilename, detectDeviceTypeFromDimensions } from './types';

export interface BannerService {
  uploadFile: (file: File) => Promise<string | null>;
  removeUploadingFile: (fileName: string) => void;
  uploadingFiles: Record<string, { name: string; progress: number; error?: string }>;
}

/**
 * Process a file, detect its type, upload to S3, and add to the appropriate banner group
 */
export const processFile = async (
  file: File,
  banner: BannerService,
  uploadedFiles: BannerUploadData[],
  setUploadedFiles: React.Dispatch<React.SetStateAction<BannerUploadData[]>>,
  dateRange: { fromDate: Date; toDate: Date },
): Promise<void> => {
  // 1. Determine the device type from filename
  let deviceType = detectDeviceTypeFromFilename(file.name);

  // 2. If no clear match from filename, check actual image dimensions
  if (!deviceType) {
    deviceType = await detectDeviceTypeFromDimensions();
  }

  // 3. Extract base name (remove dimensions and extension)
  const baseName = file.name.replace(/_\d+x\d+\.\w+$/, '').replace(/\.\w+$/, '');

  // 4. Upload the file to S3
  const s3Url = await banner.uploadFile(file);
  if (!s3Url) {
    throw new Error(`Failed to upload ${file.name}`);
  }

  console.info(`Uploaded ${file.name} as ${deviceType} image to ${s3Url}`);

  // 5. Find if we already have a banner group with this base name
  const existingGroupIndex = uploadedFiles.findIndex(
    (item) => item.title.toLowerCase() === baseName.replace(/_/g, ' ').trim().toLowerCase(),
  );

  if (existingGroupIndex >= 0) {
    // Add this image to an existing banner group
    setUploadedFiles((prev) => {
      const updatedFiles = [...prev];
      const currentGroup = updatedFiles[existingGroupIndex];

      if (!currentGroup) {
        return prev;
      } // Guard against undefined

      // Ensure imageFiles is initialized
      if (!currentGroup.imageFiles) {
        currentGroup.imageFiles = {};
      }

      // Update the image URL based on device type
      if (deviceType === DeviceType.DESKTOP) {
        currentGroup.imageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.DESKTOP] = file;
      } else if (deviceType === DeviceType.TABLET) {
        currentGroup.tabletImageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.TABLET] = file;
      } else if (deviceType === DeviceType.MOBILE) {
        currentGroup.mobileImageUrl = s3Url;
        currentGroup.imageFiles[DeviceType.MOBILE] = file;
      }

      return updatedFiles;
    });
  } else {
    // Create a new banner group
    const newBanner: BannerUploadData = {
      title: baseName.replace(/_/g, ' ').trim(),
      description: '',
      linkUrl: '',
      location: '',
      active: true,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      imageFiles: {},
    };

    // Set the appropriate image URL based on device type
    if (deviceType === DeviceType.DESKTOP) {
      newBanner.imageUrl = s3Url;
      newBanner.imageFiles[DeviceType.DESKTOP] = file;
    } else if (deviceType === DeviceType.TABLET) {
      newBanner.tabletImageUrl = s3Url;
      newBanner.imageFiles[DeviceType.TABLET] = file;
    } else if (deviceType === DeviceType.MOBILE) {
      newBanner.mobileImageUrl = s3Url;
      newBanner.imageFiles[DeviceType.MOBILE] = file;
    }

    setUploadedFiles((prev) => [...prev, newBanner]);
  }
};
